/**
 * Cart — full-page, staged checkout (user decision):
 *
 *   1. Items     what's being purchased, total material cost, and the
 *                monthly recurring cost it will add once deployed
 *   2. Shipping  select the organization's saved address or enter a new one
 *                (billing address separately when different)
 *   3. Payment   credit card on file (Stripe) or terms via the provider
 *   4. Review    everything on one screen — confirm and submit
 *
 * Cart contents persist per browser (same store the Purchase Material page
 * writes). Submitting splits the cart into one order per hardware provider.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { useAuth } from '../context/AuthContext';
import { isCompanyAdmin } from '../services/users';
import {
  checkoutCart, loadCart, saveCart, paymentReasonCopy, getStoreProduct,
  CartItem, CheckoutAddress, PlaceOrderResult,
} from '../services/store';
import { getCompany } from '../services/companies';
import { getPaymentMethods } from '../services/billing';
import { formatMoney } from '../services/deviceBoards';
import { assetPreviewUrl } from '../services/listings';
import { AuthImage } from '../components/AuthImage';
import {
  PageContainer, PageHeader, PageTitle, SectionTitle, DetailCard,
  TableContainer, StyledTable, Th, Td, StatusChip,
  EmptyState, MutedText, Caption, Row,
  Button, LinkButton, Input, FormGroup, Label, HelperText, ErrorAlert,
} from '../components/styled';

type Step = 'items' | 'shipping' | 'payment' | 'review' | 'done';

const STEPS: { id: Step; label: string }[] = [
  { id: 'items',    label: '1 · Items' },
  { id: 'shipping', label: '2 · Shipping' },
  { id: 'payment',  label: '3 · Payment' },
  { id: 'review',   label: '4 · Review' },
];

const StepBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  flex-wrap: wrap;
`;

const StepPill = styled.span<{ active: boolean; done: boolean }>`
  padding: ${({ theme }) => theme.spacing(1, 3)};
  border-radius: 999px;
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  border: 1px solid ${({ theme, active }) => (active ? theme.accent.primary : theme.border.color.light)};
  color: ${({ theme, active, done }) => (active ? theme.accent.primary : done ? theme.font.color.primary : theme.font.color.tertiary)};
`;

const TotalsCard = styled(DetailCard)`
  max-width: 420px;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(1)} 0;

  strong { font-size: ${({ theme }) => theme.font.size.lg}; }
`;

const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing(6)};
  max-width: 720px;
`;

const FormCard = styled(DetailCard)`
  max-width: 720px;
`;

const AddrGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 ${({ theme }) => theme.spacing(3)};
`;

const Thumb = styled.div`
  width: 48px;
  height: 36px;
  border-radius: ${({ theme }) => theme.border.radius.md};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  background: ${({ theme }) => theme.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img { max-width: 100%; max-height: 100%; object-fit: contain; }
`;

const EMPTY_ADDR: CheckoutAddress = { name: '', company: '', line1: '', line2: '', city: '', state: '', postal: '', country: 'US', phone: '' };

function formatAddr(a: CheckoutAddress | Record<string, string>): string {
  return [a.name, a.company, a.line1, a.line2, `${a.city}, ${a.state} ${a.postal}`].filter(Boolean).join(' · ');
}

function AddressFields({ value, onChange }: {
  value: CheckoutAddress;
  onChange: (a: CheckoutAddress) => void;
}) {
  const set = (key: keyof CheckoutAddress) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [key]: e.target.value });
  return (
    <>
      <AddrGrid>
        <FormGroup>
          <Label>Full name</Label>
          <Input value={value.name} onChange={set('name')} required />
        </FormGroup>
        <FormGroup>
          <Label>Company</Label>
          <Input value={value.company || ''} onChange={set('company')} />
        </FormGroup>
      </AddrGrid>
      <FormGroup>
        <Label>Address line 1</Label>
        <Input value={value.line1} onChange={set('line1')} required />
      </FormGroup>
      <FormGroup>
        <Label>Address line 2</Label>
        <Input value={value.line2 || ''} onChange={set('line2')} />
      </FormGroup>
      <AddrGrid>
        <FormGroup>
          <Label>City</Label>
          <Input value={value.city} onChange={set('city')} required />
        </FormGroup>
        <FormGroup>
          <Label>State</Label>
          <Input value={value.state} onChange={set('state')} required />
        </FormGroup>
        <FormGroup>
          <Label>ZIP / Postal</Label>
          <Input value={value.postal} onChange={set('postal')} required />
        </FormGroup>
        <FormGroup>
          <Label>Phone</Label>
          <Input value={value.phone || ''} onChange={set('phone')} />
        </FormGroup>
      </AddrGrid>
    </>
  );
}

function Cart() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('items');
  const [cart, setCartState] = useState<CartItem[]>(() => loadCart());
  const setCart = (items: CartItem[]) => { setCartState(items); saveCart(items); };

  const [addrMode, setAddrMode] = useState<'saved' | 'new'>('new');
  const [shipping, setShipping] = useState<CheckoutAddress>(EMPTY_ADDR);
  const [billingSame, setBillingSame] = useState(true);
  const [billing, setBilling] = useState<CheckoutAddress>(EMPTY_ADDR);
  const [saveDefault, setSaveDefault] = useState(true);
  const [payment, setPayment] = useState<'card' | 'terms'>('card');
  const [stepError, setStepError] = useState<string[]>([]);
  const [results, setResults] = useState<PlaceOrderResult[] | null>(null);

  const companyId = user?.company_id ? String(user.company_id) : '';

  const { data: company } = useQuery({
    queryKey: ['cart-company-defaults', companyId],
    queryFn: () => getCompany(companyId as any),
    enabled: !!companyId,
  });
  const savedShipping = (company as any)?.default_shipping as CheckoutAddress | undefined;
  const savedBilling = (company as any)?.default_billing as CheckoutAddress | undefined;

  const { data: pm } = useQuery({
    queryKey: ['system-payment-methods', companyId],
    queryFn: () => getPaymentMethods(companyId),
    enabled: !!companyId,
  });
  const defaultCard = pm?.methods?.find((m) => m.id === pm.defaultPaymentMethodId) || pm?.methods?.[0];

  useEffect(() => {
    if (savedShipping?.line1) setAddrMode('saved');
  }, [savedShipping?.line1]);

  const materialTotal = cart.reduce((s, it) => s + it.unitPriceCents * it.quantity, 0);
  const monthlyTotal = cart.reduce((s, it) => s + it.monthlyFeeCents * it.quantity, 0);
  const currency = cart[0]?.currency || 'USD';
  const providerCount = new Set(cart.map((it) => it.manufacturerId)).size;
  const termsAvailable = cart.length > 0 && cart.every((it) => it.providerOffersTerms);
  const termsRefusers = [...new Set(cart.filter((it) => !it.providerOffersTerms).map((it) => it.providerName))];

  // Cart lines are a client-side snapshot (price/availability at add-to-cart
  // time). Re-check each against the live catalog so a SKU that went
  // unavailable or was repriced surfaces here, on Items, instead of only as
  // a 409 at Review — or worse, a silently different charge than shown.
  const liveProductQueries = useQueries({
    queries: cart.map((it) => ({
      queryKey: ['cart-live-product', it.variantId],
      queryFn: () => getStoreProduct(it.variantId).catch(() => null),
      staleTime: 30_000,
    })),
  });
  type LiveIssue =
    | { kind: 'unavailable' }
    | { kind: 'repriced'; newUnitPriceCents: number; newMonthlyFeeCents: number };
  const liveIssues: (LiveIssue | null)[] = cart.map((it, i) => {
    const product = liveProductQueries[i]?.data;
    if (!liveProductQueries[i]?.isFetched) return null;
    if (!product || product.status !== 'available' || !product.current_price) return { kind: 'unavailable' };
    const { hardware_price_cents, monthly_fee_cents } = product.current_price;
    if (hardware_price_cents !== it.unitPriceCents || monthly_fee_cents !== it.monthlyFeeCents) {
      return { kind: 'repriced', newUnitPriceCents: hardware_price_cents, newMonthlyFeeCents: monthly_fee_cents };
    }
    return null;
  });
  const hasUnavailable = liveIssues.some((issue) => issue?.kind === 'unavailable');

  const updatePrice = (variantId: number, unitPriceCents: number, monthlyFeeCents: number) =>
    setCart(cart.map((it) => (it.variantId === variantId ? { ...it, unitPriceCents, monthlyFeeCents } : it)));

  const effectiveShipping: CheckoutAddress = addrMode === 'saved' && savedShipping ? { ...EMPTY_ADDR, ...savedShipping } : shipping;

  const setQty = (variantId: number, qty: number) =>
    setCart(cart.map((it) => (it.variantId === variantId ? { ...it, quantity: Math.max(1, qty) } : it)));
  const remove = (variantId: number) => setCart(cart.filter((it) => it.variantId !== variantId));

  const mutation = useMutation({
    mutationFn: () => checkoutCart({
      items: cart.map((it) => ({ variantId: it.variantId, quantity: it.quantity })),
      paymentMethod: payment,
      shipping: effectiveShipping,
      billing: billingSame ? null : billing,
      saveAsDefault: addrMode === 'new' ? saveDefault : false,
    }),
    onSuccess: ({ orders }) => {
      queryClient.invalidateQueries({ queryKey: ['store-orders'] } as any);
      queryClient.invalidateQueries({ queryKey: ['cart-company-defaults', companyId] } as any);
      setResults(orders);
      setCart([]);
      setStep('done');
    },
    onError: (err: any) => {
      const body = err.response?.data;
      setStepError([body?.error || 'Checkout failed', ...(body?.details || [])]);
    },
  });

  const advance = () => {
    setStepError([]);
    if (step === 'items') {
      if (cart.length === 0) return setStepError(['Your cart is empty.']);
      if (hasUnavailable) return setStepError(['Remove the unavailable item(s) below before continuing.']);
      setStep('shipping');
    } else if (step === 'shipping') {
      const a = effectiveShipping;
      const missing = (['name', 'line1', 'city', 'state', 'postal'] as const).filter((k) => !a[k]?.trim());
      if (missing.length) return setStepError([`Shipping address needs: ${missing.join(', ')}`]);
      if (!billingSame) {
        const missB = (['name', 'line1', 'city', 'state', 'postal'] as const).filter((k) => !billing[k]?.trim());
        if (missB.length) return setStepError([`Billing address needs: ${missB.join(', ')}`]);
      }
      setStep('payment');
    } else if (step === 'payment') {
      if (payment === 'terms' && !termsAvailable) return setStepError(['Terms are not available for this cart.']);
      setStep('review');
    }
  };
  const back = () => {
    setStepError([]);
    setStep(step === 'shipping' ? 'items' : step === 'payment' ? 'shipping' : 'payment');
  };

  if (!isCompanyAdmin(user)) {
    return (
      <PageContainer>
        <PageHeader><PageTitle>Cart</PageTitle></PageHeader>
        <EmptyState><p>Hardware purchasing is available to company admins.</p></EmptyState>
      </PageContainer>
    );
  }

  const stepIndex = STEPS.findIndex((s) => s.id === step);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Checkout</PageTitle>
        <Button variant="secondary" onClick={() => navigate('/store')}>Keep shopping</Button>
      </PageHeader>

      {step !== 'done' && (
        <StepBar>
          {STEPS.map((s, i) => (
            <StepPill key={s.id} active={s.id === step} done={i < stepIndex}>{s.label}</StepPill>
          ))}
        </StepBar>
      )}

      {stepError.length > 0 && (
        <ErrorAlert as="div">{stepError.map((line, i) => <div key={i}>{line}</div>)}</ErrorAlert>
      )}

      {/* ── 1 · Items ── */}
      {step === 'items' && (
        cart.length === 0 ? (
          <EmptyState>
            <p>Your cart is empty.</p>
            <MutedText><Link to="/store">Browse the catalog</Link> to add hardware.</MutedText>
          </EmptyState>
        ) : (
          <>
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th></Th>
                    <Th>SKU</Th>
                    <Th>Product</Th>
                    <Th>Provider</Th>
                    <Th>Qty</Th>
                    <Th>Unit price</Th>
                    <Th>Monthly / device</Th>
                    <Th>Line total</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((it, i) => {
                    const issue = liveIssues[i];
                    return (
                    <tr key={it.variantId}>
                      <Td>
                        <Thumb>
                          {it.heroAssetId
                            ? <AuthImage src={assetPreviewUrl(it.boardId, it.heroAssetId)} alt={it.name} fallback={<Caption>—</Caption>} />
                            : <Caption>—</Caption>}
                        </Thumb>
                      </Td>
                      <Td><strong>{it.sku}</strong></Td>
                      <Td>
                        {it.name}
                        {issue?.kind === 'unavailable' && (
                          <div><StatusChip variant="critical">No longer available</StatusChip></div>
                        )}
                        {issue?.kind === 'repriced' && (
                          <div>
                            <StatusChip variant="warning">Price changed</StatusChip>
                            {' '}
                            <LinkButton
                              type="button"
                              onClick={() => updatePrice(it.variantId, issue.newUnitPriceCents, issue.newMonthlyFeeCents)}
                            >
                              Use new price ({formatMoney(issue.newUnitPriceCents, it.currency)})
                            </LinkButton>
                          </div>
                        )}
                      </Td>
                      <Td>{it.providerName}</Td>
                      <Td>
                        <Input
                          type="number" min={1} max={500} value={it.quantity}
                          onChange={(e) => setQty(it.variantId, parseInt(e.target.value, 10) || 1)}
                          style={{ maxWidth: 76 }}
                        />
                      </Td>
                      <Td>{formatMoney(it.unitPriceCents, it.currency)}</Td>
                      <Td>{it.monthlyFeeCents > 0 ? formatMoney(it.monthlyFeeCents, it.currency) : <MutedText>none</MutedText>}</Td>
                      <Td>{formatMoney(it.unitPriceCents * it.quantity, it.currency)}</Td>
                      <Td><LinkButton type="button" onClick={() => remove(it.variantId)}>Remove</LinkButton></Td>
                    </tr>
                    );
                  })}
                </tbody>
              </StyledTable>
            </TableContainer>

            <TotalsCard>
              <TotalRow>
                <span>Material cost</span>
                <strong>{formatMoney(materialTotal, currency)}</strong>
              </TotalRow>
              <TotalRow>
                <span>Monthly recurring</span>
                <strong>{formatMoney(monthlyTotal, currency)}/mo</strong>
              </TotalRow>
              <HelperText>
                Recurring service fees start per device once it is deployed.
                {providerCount > 1 && ` This cart spans ${providerCount} hardware providers and becomes ${providerCount} orders.`}
              </HelperText>
            </TotalsCard>
          </>
        )
      )}

      {/* ── 2 · Shipping ── */}
      {step === 'shipping' && (
        <FormCard>
          <SectionTitle>Shipping address</SectionTitle>
          {savedShipping?.line1 && (
            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="radio" name="addrmode" checked={addrMode === 'saved'} onChange={() => setAddrMode('saved')} />
                Use saved address
              </Label>
              {addrMode === 'saved' && <MutedText style={{ marginLeft: 24 }}>{formatAddr(savedShipping)}</MutedText>}
              <Label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <input type="radio" name="addrmode" checked={addrMode === 'new'} onChange={() => setAddrMode('new')} />
                Enter a new address
              </Label>
            </FormGroup>
          )}
          {(addrMode === 'new' || !savedShipping?.line1) && (
            <>
              <AddressFields value={shipping} onChange={setShipping} />
              <FormGroup>
                <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} />
                  Save as my organization's default
                </Label>
              </FormGroup>
            </>
          )}

          <FormGroup>
            <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} />
              Billing address same as shipping
            </Label>
          </FormGroup>
          {!billingSame && (
            <>
              <SectionTitle>Billing address</SectionTitle>
              {savedBilling?.line1 && !billing.line1 && (
                <FormGroup>
                  <LinkButton type="button" onClick={() => setBilling({ ...EMPTY_ADDR, ...savedBilling })}>
                    Use saved billing address ({formatAddr(savedBilling)})
                  </LinkButton>
                </FormGroup>
              )}
              <AddressFields value={billing} onChange={setBilling} />
            </>
          )}
        </FormCard>
      )}

      {/* ── 3 · Payment ── */}
      {step === 'payment' && (
        <FormCard>
          <SectionTitle>Payment method</SectionTitle>
          <FormGroup>
            <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="radio" name="payment" checked={payment === 'card'} onChange={() => setPayment('card')} />
              Credit card on file (Stripe)
            </Label>
            <div style={{ marginLeft: 24 }}>
              {defaultCard
                ? <MutedText style={{ textTransform: 'capitalize' }}>{defaultCard.brand} •••• {defaultCard.last4}</MutedText>
                : <MutedText>No card on file — the order will be recorded pending until one is added under <Link to="/purchases?tab=payment">Purchases → Payment methods</Link>.</MutedText>}
            </div>
            <Label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <input
                type="radio" name="payment"
                checked={payment === 'terms'}
                disabled={!termsAvailable}
                onChange={() => setPayment('terms')}
              />
              Terms via the hardware provider
            </Label>
            <div style={{ marginLeft: 24 }}>
              {termsAvailable
                ? <MutedText>Nothing is charged now — each provider reviews and accepts the order, then bills you directly on their terms.</MutedText>
                : termsRefusers.length > 0
                  ? <MutedText>Unavailable: {termsRefusers.join(', ')} do{termsRefusers.length === 1 ? 'es' : ''} not offer terms.</MutedText>
                  : null}
            </div>
          </FormGroup>
        </FormCard>
      )}

      {/* ── 4 · Review ── */}
      {step === 'review' && (
        <FormCard>
          <SectionTitle>Review your order</SectionTitle>
          <FormGroup>
            <Caption>Items</Caption>
            {cart.map((it) => (
              <div key={it.variantId}>
                {it.quantity} × {it.sku} — {it.name}
                <MutedText> · {it.providerName} · {formatMoney(it.unitPriceCents * it.quantity, it.currency)}</MutedText>
              </div>
            ))}
          </FormGroup>
          <FormGroup>
            <Caption>Ship to</Caption>
            <div>{formatAddr(effectiveShipping)}</div>
            {!billingSame && <MutedText>Bill to: {formatAddr(billing)}</MutedText>}
          </FormGroup>
          <FormGroup>
            <Caption>Payment</Caption>
            <div>
              {payment === 'card'
                ? <>Credit card on file{defaultCard && <MutedText style={{ textTransform: 'capitalize' }}> — {defaultCard.brand} •••• {defaultCard.last4}</MutedText>}</>
                : 'Terms via the hardware provider'}
            </div>
          </FormGroup>
          <TotalRow>
            <span>Material cost{payment === 'card' ? ' (charged today)' : ' (billed on terms)'}</span>
            <strong>{formatMoney(materialTotal, currency)}</strong>
          </TotalRow>
          <TotalRow>
            <span>Monthly recurring once deployed</span>
            <strong>{formatMoney(monthlyTotal, currency)}/mo</strong>
          </TotalRow>
        </FormCard>
      )}

      {/* ── Done ── */}
      {step === 'done' && results && (
        <FormCard>
          <SectionTitle>Order placed</SectionTitle>
          {results.map(({ order, payment: p }) => (
            <div key={order.id} style={{ marginBottom: 10 }}>
              <strong>{order.order_number}</strong>
              {' '}<StatusChip variant={order.status === 'paid' ? 'green' : 'warning'}>{order.status}</StatusChip>
              <div>
                <MutedText>
                  {p.charged
                    ? `Charged ${formatMoney(order.subtotal_cents, order.currency)} to your card on file.`
                    : paymentReasonCopy(p.reason)}
                </MutedText>
              </div>
            </div>
          ))}
          {monthlyTotal > 0 && (
            <HelperText>Recurring service billing begins per device as each one deploys.</HelperText>
          )}
          <Row gap={2} style={{ marginTop: 12 }}>
            <Button onClick={() => navigate('/purchases')}>View my orders</Button>
            <Button variant="secondary" onClick={() => navigate('/store')}>Back to the store</Button>
          </Row>
        </FormCard>
      )}

      {step !== 'done' && cart.length > 0 && (
        <NavRow>
          {step !== 'items'
            ? <Button variant="secondary" onClick={back}>Back</Button>
            : <span />}
          {step !== 'review' ? (
            <Button onClick={advance}>
              {step === 'items' ? 'Confirm items' : step === 'shipping' ? 'Continue to payment' : 'Review order'}
            </Button>
          ) : (
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? 'Placing order…' : `Submit — ${formatMoney(materialTotal, currency)}`}
            </Button>
          )}
        </NavRow>
      )}
    </PageContainer>
  );
}

export default Cart;
